import { GridItem } from '@/dashboard/mui/styledComponent';
import { BriefcaseIcon, OfficeBuildingIcon } from '@heroicons/react/solid';
import {
  PolicyDetailsResponse,
  formatCurrency,
  useCountryStore,
  useCoverStore,
} from '@jove-frontend/uikit';
import { Box, Grid, Typography } from '@mui/material';

type BusinessInsuranceProductProps = {
  policy: PolicyDetailsResponse;
};

const BusinessInsuranceProduct = ({
  policy,
}: BusinessInsuranceProductProps) => {
  const [{ covers }] = useCoverStore(({ state }) => [state]);
  const [{ currencies }] = useCountryStore(({ state }) => [state]);

  const getCoverIcon = (slug: string) => {
    switch (slug) {
      case 'professional-indemnity':
        return <BriefcaseIcon className="text-black-secondary w-5" />;
      case 'public-liability':
        return (
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-black-secondary"
          >
            <g clipPath="url(#clip0_631_19814)">
              <path
                d="M7.62986 3.29439C7.62986 4.86189 6.35924 6.13127 4.79049 6.13127C3.22299 6.13127 1.95361 4.86189 1.95361 3.29439C1.95361 1.72564 3.22299 0.455017 4.79049 0.455017C6.35924 0.455642 7.62986 1.72627 7.62986 3.29439Z"
                fill="currentColor"
              />
              <path
                d="M20.0227 11.4369C19.9483 11.3406 19.8896 11.2906 19.8896 11.2906C17.6446 9.20063 15.0077 9.20688 13.0021 9.68688C11.6739 9.11938 10.6427 8.41251 9.87457 7.75813C11.5308 6.65126 12.2414 5.00563 12.6321 3.64813C12.6514 3.57876 12.7064 3.36876 12.7064 3.36876C12.7564 3.08938 12.8552 2.10626 11.8714 1.76001C10.7189 1.35501 10.3421 2.61688 10.3421 2.61688C10.3152 2.71438 10.2983 2.81251 10.2689 2.90376C9.70145 4.77501 8.74082 5.72251 7.01207 6.13501C3.31707 7.02001 1.72145 8.92313 1.07582 10.0656C1.02957 10.15 0.730199 10.6444 0.632699 11.0888C0.553324 11.4406 0.544574 12.2888 1.42957 12.6388C2.3327 12.9963 2.78457 12.2863 2.93207 11.9781C2.9702 11.8975 3.03707 11.74 3.09082 11.63C3.2827 11.2469 3.84145 10.37 5.21957 9.61438C6.22645 10.8131 8.05145 12.5931 10.8333 13.8969C11.9796 14.6488 13.9546 16.2306 14.1221 18.2925C14.1221 18.2925 14.1602 18.5194 14.1846 18.5894C14.3152 18.9838 14.6677 19.6406 15.5433 19.5344C16.2771 19.445 16.5258 18.9225 16.6077 18.5138C16.6383 18.3475 16.6527 18.2 16.6514 18.1075C16.4452 15.5681 14.8071 13.6619 13.3496 12.4494C14.8108 12.0513 16.7446 11.9538 18.2739 13.3856C18.2739 13.3856 18.3827 13.4588 18.4033 13.4725C18.7121 13.6838 19.4677 14.0619 20.0877 13.2738C20.6989 12.4975 20.2633 11.7494 20.0227 11.4369Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_631_19814">
                <rect
                  width="20"
                  height="20"
                  fill="white"
                  transform="translate(0.5)"
                />
              </clipPath>
            </defs>
          </svg>
        );
      case 'employers-liability':
        return <OfficeBuildingIcon className="text-black-secondary w-5" />;
      default:
        return <BriefcaseIcon className="text-black-secondary w-5" />;
        break;
    }
  };

  if (!covers || !currencies) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {policy?.covers?.map((item, index) => {
        const policyCover = covers.find((c) => c.id === item.coverId);
        const currency = currencies?.find(
          (curr) => curr.id === item.currencyId
        );

        const coverText = formatCurrency({
          amount: item.insuredAmount,
          currency: currency?.code,
        });
        const moneyText = `${formatCurrency({
          amount: item.amount,
          currency: currency?.code,
        })}/mo`;
        return (
          <Grid item key={index} xs={12} md={6}>
            <GridItem>
              <Box className="bg-gray-light flex flex-row items-center rounded-lg px-3 py-4">
                <Box className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#d6d6d6] p-2">
                  <Box className="text-black">
                    {getCoverIcon(policyCover?.slug)}
                  </Box>
                </Box>
                <Box className="ml-3 flex w-0 flex-1 flex-col">
                  <Box className="flex flex-row justify-between">
                    <Typography className="font-bold" variant="subtitle1">
                      {policyCover?.name}
                    </Typography>
                    <Typography className="font-bold" variant="subtitle1">
                      {moneyText}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2">{coverText}</Typography>
                </Box>
              </Box>
            </GridItem>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default BusinessInsuranceProduct;
